<template>
  <div class="qa_record_page">
    <van-nav-bar
      title="答题记录"
      left-arrow
      @click-left="onClickLeft"
      fixed
      placeholder
      safe-area-inset-top
    />

    <div class="record_list" v-if="chatList && chatList.length">
      <div class="record_item" v-for="(item, index) in chatList" :key="index">
        <!-- 序号和标题区域 -->
        <div class="question_header">
          <span class="index_num">{{ index + 1 }}</span>
          <div class="question_text">
            <div class="main_text">{{ item.text || item.sys || '' }}</div>
            <div class="sub_text" v-if="item.subText">{{ item.subText }}</div>
          </div>
        </div>

        <!-- 答案区域 -->
        <div class="answer_content">
          <!-- 1. 语音类型 (recording) -->
          <template v-if="item.type === 'recording'">
            <div class="voice_list" v-if="item.voice && item.voice.length">
              <div class="voice_item" v-for="(voiceUrl, vIndex) in item.voice" :key="vIndex" @click="playVoice(voiceUrl)">
                <div class="voice_bubble">
                  <!-- 模拟声波纹的竖线 -->
                  <div class="wave_bars">
                    <span class="bar bar1"></span>
                    <span class="bar bar2"></span>
                    <span class="bar bar3"></span>
                    <span class="bar bar2"></span>
                    <span class="bar bar1"></span>
                  </div>
                </div>
                <div class="voice_icon">
                  <van-icon name="volume-o" size="20px" color="#2F6BFF" />
                </div>
              </div>
            </div>
            <!-- 用户回答兜底显示 -->
            <div class="user_text" v-else-if="item.user">{{ item.user }}</div>
          </template>

          <!-- 2. 图片类型 (tongue / face) -->
          <template v-else-if="item.type === 'tongue' || item.type === 'face'">
            <div class="img_scroll_wrap" v-if="item.imgs && item.imgs.length">
              <div class="img_list">
                <van-image
                  v-for="(imgUrl, imgIndex) in parseImgs(item.imgs)"
                  :key="imgIndex"
                  :src="imgUrl"
                  fit="cover"
                  class="preview_img"
                  @click="previewImage(parseImgs(item.imgs), imgIndex)"
                />
              </div>
            </div>
            <!-- 用户回答兜底显示 -->
            <div class="user_text" v-else-if="item.user">{{ item.user }}</div>
          </template>

          <!-- 3. 普通文本类型 (其他) -->
          <template v-else>
            <div class="user_text">{{ item.user || '未回答' }}</div>
          </template>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <van-empty v-else description="暂无答题记录" />

    <!-- 隐藏的音频播放器 -->
    <audio ref="audioPlayer" style="display: none;"></audio>
  </div>
</template>

<script>
import { NavBar, ImagePreview, Image as VanImage, Icon, Empty } from 'vant';

export default {
  name: 'QaRecord',
  components: {
    [NavBar.name]: NavBar,
    [VanImage.name]: VanImage,
    [Icon.name]: Icon,
    [Empty.name]: Empty
  },
  data() {
    return {
      chatList: []
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    onClickLeft() {
      this.$router.go(-1);
    },
    loadData() {
      // 1. 尝试从 sessionStorage 获取透传的 chat 数组
      const chatStr = sessionStorage.getItem("MY_AI_QA_RECORD");
      if (chatStr) {
        try {
          this.chatList = JSON.parse(chatStr);
        } catch (e) {
          console.error("解析答题记录失败", e);
        }
      }
    },
    parseImgs(imgs) {
      // 处理后端返回的图片字符串可能包含的反引号和空格
      if (!Array.isArray(imgs)) return [];
      return imgs.map(img => {
        if (typeof img === 'string') {
          return img.replace(/`/g, '').trim();
        }
        return img;
      }).filter(img => !!img);
    },
    previewImage(images, startPosition) {
      ImagePreview({
        images,
        startPosition,
        closeable: true,
      });
    },
    playVoice(voiceUrl) {
      // 处理反引号
      const url = voiceUrl.replace(/`/g, '').trim();
      if (!url) return;
      
      const audio = this.$refs.audioPlayer;
      if (audio.src !== url) {
        audio.src = url;
      }
      
      if (audio.paused) {
        audio.play().catch(err => {
          this.$toast('音频播放失败');
          console.error(err);
        });
      } else {
        audio.pause();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.qa_record_page {
  background: #fff;
  min-height: 100vh;
}

.record_list {
  padding: 0 16px 24px;
}

.record_item {
  border-bottom: 1px dashed #eee;
  padding: 24px 0;
  
  &:last-child {
    border-bottom: none;
  }
}

/* 序号和问题部分 */
.question_header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.index_num {
  font-size: 18px;
  color: #333;
  margin-right: 12px;
  line-height: 1.4;
  font-weight: 500;
  min-width: 20px;
}

.question_text {
  flex: 1;
}

.main_text {
  font-size: 16px;
  color: #333;
  line-height: 1.5;
}

.sub_text {
  font-size: 14px;
  color: #999;
  margin-top: 4px;
  line-height: 1.5;
}

/* 答案区域部分 */
.answer_content {
  padding-left: 32px; /* 与上方的 index_num 对齐 */
}

/* 1. 文本答案 */
.user_text {
  font-size: 16px;
  color: #333;
  line-height: 1.5;
}

/* 2. 图片滑动区域 */
.img_scroll_wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

.img_list {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
}

.preview_img {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #f7f8fa;
  border: 1px solid #eee;
}

/* 3. 语音播放气泡 */
.voice_list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.voice_item {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.voice_bubble {
  background: #007BFF;
  border-radius: 6px;
  padding: 10px 20px;
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  position: relative;
  
  /* 气泡小尾巴 */
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #007BFF;
  }
}

.wave_bars {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 16px;
}

.bar {
  width: 2px;
  background: #fff;
  border-radius: 2px;
  animation: wave 1s infinite ease-in-out;
}

.bar1 { height: 6px; animation-delay: 0s; }
.bar2 { height: 10px; animation-delay: 0.2s; }
.bar3 { height: 16px; animation-delay: 0.4s; }

@keyframes wave {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.5); }
}

.voice_icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #F0F5FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
</style>
